import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
    amountDisplay,
    englishToBengaliNumber,
    getMonthName,
    getUserTimezone,
    isDarkModeOn,
} from '../../util/helper';
import { useLanguage } from '../../context/LanguageContext';

const DayWiseLineChart = ({
    expenseData,
    daysInMonth,
    header,
    title,
    performanceMode = {},
    xAxisLabels = null,
}) => {
    const { language, t } = useLanguage();
    const isCompact = Boolean(performanceMode.compact);
    const disableAnimation = Boolean(performanceMode.disableAnimation);
    const disableEffects = Boolean(performanceMode.disableEffects);
    const chartHeight = isCompact ? 235 : 320;
    dayjs.extend(utc);
    dayjs.extend(timezone);
    const currentMonth = dayjs().tz(getUserTimezone()).format('MMM');
    const isDark = isDarkModeOn();
    const labelColor = isDark ? '#9ba3aa' : '#958f9f';
    const gridColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(149,143,159,0.18)';

    // Generate day labels with translations - recalculate when language changes
    const dayLabels = useMemo(() => {
        return Array.from({ length: daysInMonth }, (_, i) => {
            const dayNumber = i + 1;
            const displayNumber = language === 'bn' ? englishToBengaliNumber(dayNumber) : dayNumber;
            return `${t('day')} ${displayNumber}`;
        });
    }, [daysInMonth, language, t]);
    const chartLabels = useMemo(() => {
        if (Array.isArray(xAxisLabels) && xAxisLabels.length > 0) {
            return xAxisLabels;
        }

        return dayLabels;
    }, [dayLabels, xAxisLabels]);
    const tickAmount = useMemo(() => {
        if (chartLabels.length <= 1) return 1;
        return Math.min(chartLabels.length - 1, 6);
    }, [chartLabels]);

    const series = [
        {
            name: title ? title : t('totalExpense'),
            data: expenseData,
        },
    ];

    const options = {
        chart: {
            type: 'area',
            height: 320,
            fontFamily: 'Lato, sans-serif',
            background: 'transparent',
            toolbar: { show: false },
            zoom: { enabled: false },
            animations: {
                enabled: !disableAnimation,
                easing: 'easeinout',
                speed: disableAnimation ? 1 : 450,
                animateGradually: {
                    enabled: !disableAnimation,
                    delay: disableAnimation ? 0 : 50,
                },
            },
            dropShadow: {
                enabled: !disableEffects,
                top: 6,
                left: 0,
                blur: 8,
                opacity: 0.18,
                color: '#622cfd',
            },
        },
        colors: ['#622cfd'],
        dataLabels: { enabled: false },
        stroke: {
            curve: 'smooth',
            width: 2.5,
            lineCap: 'round',
        },
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                opacityFrom: isDark ? 0.35 : 0.28,
                opacityTo: 0.02,
                stops: [0, 90, 100],
            },
        },
        xaxis: {
            categories: chartLabels,
            axisBorder: { show: false },
            axisTicks: { show: false },
            labels: {
                show: true,
                rotate: 0,
                hideOverlappingLabels: true,
                style: {
                    fontSize: '10px',
                    colors: labelColor,
                },
            },
            tickAmount,
            tooltip: { enabled: false },
        },
        yaxis: {
            labels: {
                formatter: (val) => `${amountDisplay(val, language)}`,
                style: {
                    fontSize: '10px',
                    colors: labelColor,
                },
            },
            tickAmount: 4,
        },
        grid: {
            strokeDashArray: 4,
            borderColor: gridColor,
            padding: { left: 6, right: 6 },
        },
        tooltip: {
            theme: isDark ? 'dark' : 'light',
            x: { show: true },
            y: {
                formatter: (val) => `৳${amountDisplay(val, language)}`,
            },
            marker: { show: false },
        },
        markers: {
            size: 0,
            strokeColors: '#fff',
            strokeWidth: 2,
            hover: { size: disableEffects ? 3 : 5, sizeOffset: 2 },
        },
        responsive: [
            {
                breakpoint: 992,
                options: {
                    chart: { height: 280 },
                },
            },
            {
                breakpoint: 768,
                options: {
                    chart: { height: 240 },
                    stroke: { width: 2 },
                    xaxis: {
                        tickAmount: 4,
                        labels: {
                            rotate: 0,
                            style: { fontSize: '9px' },
                        },
                    },
                    yaxis: {
                        tickAmount: 4,
                        labels: {
                            formatter: (val) => `${amountDisplay(val, language)}`,
                            style: { fontSize: '9px' },
                        },
                    },
                    grid: { padding: { left: 2, right: 2 } },
                },
            },
            {
                breakpoint: 576,
                options: {
                    chart: { height: 220 },
                    xaxis: {
                        tickAmount: 3,
                        labels: {
                            rotate: 0,
                            style: { fontSize: '8px' },
                        },
                    },
                    yaxis: {
                        tickAmount: 3,
                        labels: {
                            formatter: (val) => `${amountDisplay(val, language)}`,
                            style: { fontSize: '8px' },
                        },
                    },
                },
            },
        ],
    };

    return (
        <>
            {expenseData && expenseData.length > 0 && chartLabels.length > 0 && (
                <section>
                    <div className="custom-container">
                        <div className="chart">
                            <div className="d-flex justify-content-between">
                                <h3 className="fw-semibold dark-text">
                                    {header ? (
                                        header
                                    ) : (
                                        <>
                                            {t('daily')} {t('expense')} |{' '}
                                            <span className="light-text fw-normal">
                                                {getMonthName(currentMonth, t)}
                                            </span>
                                        </>
                                    )}
                                </h3>
                                <ul className="listing">
                                    <li>W</li>
                                    <li className="active">M</li>
                                    <li>Y</li>
                                </ul>
                            </div>

                            <Chart
                                options={options}
                                series={series}
                                type="area"
                                height={chartHeight}
                            />
                        </div>
                    </div>
                </section>
            )}
        </>
    );
};

export default DayWiseLineChart;
